import { useState, useCallback, useMemo } from 'react';
import { Mail, X, Plus, Send, AlertCircle, Loader2, Info } from 'lucide-react';
import type { Product } from '../../types/product';
import Button from './Button';
import ProductChipSelector from './ProductChipSelector';
import { useTranslation } from '../../i18n';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NE_OFFICE_EMAIL = 'ne-office@vivatis.de';

export interface EmailDialogSendData {
  recipients: string[];
  subject: string;
  body: string;
  selectedProducts?: Product[];
  includedTypes: string[];
}

interface EmailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (data: EmailDialogSendData) => void;
  isSending: boolean;
  defaultSubject: string;
  defaultBody: string;
  error: string | null;
  title?: string;
  /** When provided, shows a ProductChipSelector above the email fields. */
  products?: Product[];
  /** Called to regenerate subject when product selection changes. */
  getSubject?: (selected: Product[]) => string;
  /** Called to regenerate body when product selection changes. */
  getBody?: (selected: Product[]) => string;
  /** File types the cart actually carries. No entry means no checkboxes. */
  availableFileTypes?: string[];
  /** True while the file types are still on their way — keeps the block's space. */
  isLoadingFileTypes?: boolean;
}

export default function EmailDialog({
  isOpen,
  onClose,
  onSend,
  isSending,
  defaultSubject,
  defaultBody,
  error,
  title,
  products,
  getSubject,
  getBody,
  availableFileTypes,
  isLoadingFileTypes = false,
}: EmailDialogProps) {
  const { t: strings } = useTranslation();
  const [qualityHintStart, qualityHintEnd] = strings.cart.email.qualityHint.split('{email}');

  // Product selection (only used when products prop is provided)
  const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(
    () => new Set(products?.map((p) => p.id) ?? []),
  );

  const selectedProducts = useMemo(
    () => products?.filter((p) => selectedProductIds.has(p.id)) ?? [],
    [products, selectedProductIds],
  );

  // Recipient state
  const [recipients, setRecipients] = useState<string[]>([]);
  const [recipientInput, setRecipientInput] = useState('');
  const [recipientError, setRecipientError] = useState<string | null>(null);

  // Document types to hand out alongside the PDF
  const [includedTypes, setIncludedTypes] = useState<string[]>([]);

  // Email content
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);

  // Keep subject/body in sync with product selection changes
  const [lastSelectedCount, setLastSelectedCount] = useState(products?.length ?? 0);
  if (products && selectedProducts.length !== lastSelectedCount) {
    setLastSelectedCount(selectedProducts.length);
    if (getSubject) setSubject(getSubject(selectedProducts));
    if (getBody) setBody(getBody(selectedProducts));
  }

  // Reset state when dialog opens
  const [wasOpen, setWasOpen] = useState(false);
  if (isOpen && !wasOpen) {
    setWasOpen(true);
    if (products) {
      setSelectedProductIds(new Set(products.map((p) => p.id)));
      setLastSelectedCount(products.length);
    }
    setRecipients([]);
    setRecipientInput('');
    setRecipientError(null);
    setIncludedTypes([]);
    setSubject(defaultSubject);
    setBody(defaultBody);
  } else if (!isOpen && wasOpen) {
    setWasOpen(false);
  }

  const toggleProduct = useCallback((id: number) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    if (products) setSelectedProductIds(new Set(products.map((p) => p.id)));
  }, [products]);

  const deselectAll = useCallback(() => {
    setSelectedProductIds(new Set());
  }, []);

  const addRecipient = useCallback(() => {
    const trimmed = recipientInput.trim();
    setRecipientError(null);

    if (!trimmed) return;

    if (!EMAIL_REGEX.test(trimmed)) {
      setRecipientError(strings.cart.email.invalidEmail);
      return;
    }

    if (recipients.includes(trimmed)) {
      setRecipientError(strings.cart.email.duplicateEmail);
      return;
    }

    setRecipients((prev) => [...prev, trimmed]);
    setRecipientInput('');
  }, [recipientInput, recipients, strings]);

  const toggleType = useCallback((type: string) => {
    setIncludedTypes((current) =>
      current.includes(type) ? current.filter((t) => t !== type) : [...current, type],
    );
  }, []);

  const removeRecipient = useCallback((email: string) => {
    setRecipients((prev) => prev.filter((r) => r !== email));
  }, []);

  const handleSend = () => {
    if (recipients.length === 0) return;
    if (products && selectedProducts.length === 0) return;

    onSend({
      recipients,
      subject,
      body,
      includedTypes,
      ...(products ? { selectedProducts } : {}),
    });
  };

  if (!isOpen) return null;

  const canSend = recipients.length > 0
    && (!products || selectedProducts.length > 0)
    && !isSending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-vivatis-600 dark:text-vivatis-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {title ?? strings.cart.email.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Product selection (only when products are provided) */}
          {products && products.length > 0 && (
            <>
              <ProductChipSelector
                products={products}
                selectedIds={selectedProductIds}
                onToggle={toggleProduct}
                onSelectAll={selectAll}
                onDeselectAll={deselectAll}
              />
              <div className="border-t border-gray-100 dark:border-gray-700/50" />
            </>
          )}

          {/* Document types the cart's products actually carry */}
          {(isLoadingFileTypes || (availableFileTypes && availableFileTypes.length > 0)) && (
            <fieldset aria-busy={isLoadingFileTypes}>
              <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {strings.cart.email.fileTypesLegend}
              </legend>
              {isLoadingFileTypes ? (
                <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {strings.cart.email.fileTypesLoading}
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {availableFileTypes?.map((type) => (
                    <label
                      key={type}
                      className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none"
                    >
                      <input
                        type="checkbox"
                        checked={includedTypes.includes(type)}
                        onChange={() => toggleType(type)}
                        className="w-4 h-4 cursor-pointer accent-vivatis-600 focus-visible:ring-2 focus-visible:ring-vivatis-500 focus-visible:ring-offset-0"
                      />
                      {strings.cart.email.fileTypes[type as keyof typeof strings.cart.email.fileTypes] ?? type}
                    </label>
                  ))}
                </div>
              )}
              <div role="status" aria-live="polite">
                {includedTypes.includes('quality') && (
                  <p className="mt-2 flex items-start gap-2 rounded-lg bg-vivatis-50 dark:bg-vivatis-950/30 border border-vivatis-200 dark:border-vivatis-800 px-3 py-2 text-sm text-vivatis-800 dark:text-vivatis-200">
                    <Info className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
                    <span>
                      {qualityHintStart}
                      <span className="whitespace-nowrap">{NE_OFFICE_EMAIL}</span>
                      {qualityHintEnd}
                    </span>
                  </p>
                )}
              </div>
            </fieldset>
          )}

          {/* Recipients */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {strings.cart.email.recipients}
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {recipients.map((email) => (
                <span
                  key={email}
                  className="inline-flex items-center gap-1 bg-vivatis-100 dark:bg-vivatis-950/40 text-vivatis-700 dark:text-vivatis-300 rounded-full px-3 py-1 text-sm"
                >
                  {email}
                  <button
                    type="button"
                    onClick={() => removeRecipient(email)}
                    className="p-0.5 rounded-full hover:bg-vivatis-200 dark:hover:bg-vivatis-900/40 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="email"
                value={recipientInput}
                onChange={(e) => {
                  setRecipientInput(e.target.value);
                  if (recipientError) setRecipientError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addRecipient();
                  }
                }}
                placeholder={strings.cart.email.placeholder}
                className={`flex-1 px-3 py-2 text-sm rounded-lg border bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-colors ${
                  recipientError
                    ? 'border-red-400 dark:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 dark:border-gray-600 focus:ring-vivatis-500'
                }`}
              />
              <button
                type="button"
                onClick={() => {
                  if (import.meta.env.DEV && !recipientInput.trim()) {
                    const nextIndex = recipients.length + 1;
                    setRecipients((prev) => [...prev, `test${nextIndex}@test.de`]);
                  } else {
                    addRecipient();
                  }
                }}
                className="px-3 py-2 rounded-lg bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {recipientError && (
              <p className="mt-1.5 flex items-center gap-1 text-sm text-red-500 dark:text-red-400">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                {recipientError}
              </p>
            )}
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {strings.cart.email.subject}
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-vivatis-500 focus:border-transparent transition-colors"
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {strings.cart.email.message}
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-vivatis-500 focus:border-transparent transition-colors resize-y font-mono leading-relaxed"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 space-y-3 shrink-0">
          {error && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
            <Button variant="ghost" onClick={onClose}>
              {strings.cart.email.cancel}
            </Button>
            <Button
              variant="primary"
              icon={isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              onClick={handleSend}
              disabled={!canSend}
            >
              {isSending ? strings.cart.email.sending : strings.cart.email.send}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
